import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { DollarSign, TrendingUp, TrendingDown, Clock, CheckCircle } from 'lucide-react';
import AdminLayout from '../../components/layout/AdminLayout';

const Settlements = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [settlements, setSettlements] = useState([]);
  const [balance, setBalance] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    amount: '',
    type: 'payout',
    payment_method: 'bank_transfer',
    reference_number: '',
    notes: ''
  });

  useEffect(() => {
    fetchVendorSettlements();
  }, [searchTerm]);

  const fetchVendorSettlements = async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      
      const response = await api.get('/admin/settlements', { params });
      setVendors(response.data.data);
    } catch (error) {
      console.error('Error fetching settlements:', error);
      toast.error('فشل في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendorHistory = async (vendorId) => {
    try {
      const response = await api.get(`/admin/settlements/vendor/${vendorId}`);
      setSettlements(response.data.data.settlements);
      setBalance(response.data.data.balance);
      setSelectedVendor(vendorId);
    } catch (error) {
      console.error('Error fetching vendor history:', error);
      toast.error('فشل في تحميل سجل التسويات');
    }
  };

  const handleCreateSettlement = async (e) => {
    e.preventDefault();
    
    if (!selectedVendor) {
      toast.error('الرجاء اختيار بائع');
      return;
    }

    try {
      await api.post('/admin/settlements', {
        vendor_id: selectedVendor,
        ...formData
      });
      
      toast.success('تم إنشاء التسوية بنجاح');
      setShowModal(false);
      setFormData({
        amount: '',
        type: 'payout',
        payment_method: 'bank_transfer',
        reference_number: '',
        notes: ''
      });
      
      // Refresh data
      fetchVendorSettlements();
      fetchVendorHistory(selectedVendor);
    } catch (error) {
      console.error('Error creating settlement:', error);
      toast.error(error.response?.data?.message || 'فشل في إنشاء التسوية');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ar-SD', {
      style: 'currency',
      currency: 'SDG',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('ar-SD', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <LoadingSpinner />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">التسويات المالية</h1>
          <p className="mt-2 text-gray-600">إدارة التسويات والمدفوعات للبائعين</p>
        </div>

        {/* Search */}
        <div className="mb-6">
          <input
            type="text"
            placeholder="البحث عن بائع..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Vendors List */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold">البائعين</h2>
            </div>
            <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
              {vendors.map(({ vendor, balance }) => (
                <div
                  key={vendor.id}
                  onClick={() => fetchVendorHistory(vendor.id)}
                  className={`p-4 cursor-pointer hover:bg-gray-50 transition ${
                    selectedVendor === vendor.id ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold text-gray-900">{vendor.store_name}</h3>
                      <p className="text-sm text-gray-600">{vendor.user?.name}</p>
                    </div>
                    <div className="text-left">
                      <p className={`text-lg font-bold ${
                        balance.balance > 0 ? 'text-green-600' : 'text-gray-600'
                      }`}>
                        {formatCurrency(balance.balance)}
                      </p>
                      <p className="text-xs text-gray-500">الرصيد المستحق</p>
                    </div>
                  </div>
                  
                  <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <p className="text-gray-500">إجمالي المبيعات</p>
                      <p className="font-semibold">{formatCurrency(balance.total_earned)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">العمولة</p>
                      <p className="font-semibold text-red-600">{formatCurrency(balance.commission)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">المدفوع</p>
                      <p className="font-semibold text-blue-600">{formatCurrency(balance.total_paid)}</p>
                    </div>
                  </div>
                </div>
              ))}
              
              {vendors.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  لا يوجد بائعين
                </div>
              )}
            </div>
          </div>

          {/* Settlement Details */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-semibold">سجل التسويات</h2>
              {selectedVendor && balance && (
                <button
                  onClick={() => setShowModal(true)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                >
                  إنشاء تسوية جديدة
                </button>
              )}
            </div>
            
            {selectedVendor ? (
              <>
                {/* Balance Summary */}
                {balance && (
                  <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white p-4 rounded-lg shadow-sm">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600">حصة البائع</p>
                            <p className="text-2xl font-bold text-green-600">
                              {formatCurrency(balance.vendor_share)}
                            </p>
                          </div>
                          <TrendingUp className="w-8 h-8 text-green-600" />
                        </div>
                      </div>
                      
                      <div className="bg-white p-4 rounded-lg shadow-sm">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600">الرصيد المتبقي</p>
                            <p className="text-2xl font-bold text-blue-600">
                              {formatCurrency(balance.balance)}
                            </p>
                          </div>
                          <DollarSign className="w-8 h-8 text-blue-600" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Settlements History */}
                <div className="divide-y divide-gray-200 max-h-[400px] overflow-y-auto">
                  {settlements.map((settlement) => (
                    <div key={settlement.id} className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex items-start space-x-3 space-x-reverse">
                          <div className={`p-2 rounded-full ${
                            settlement.type === 'payout' ? 'bg-green-100' : 'bg-yellow-100'
                          }`}>
                            {settlement.type === 'payout' ? (
                              <TrendingDown className="w-5 h-5 text-green-600" />
                            ) : (
                              <Clock className="w-5 h-5 text-yellow-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">
                              {settlement.type === 'payout' ? 'تحويل' : 'تعديل'}
                            </p>
                            <p className="text-sm text-gray-600">
                              {formatDate(settlement.created_at)}
                            </p>
                            {settlement.reference_number && (
                              <p className="text-xs text-gray-500 mt-1">
                                رقم المرجع: {settlement.reference_number}
                              </p>
                            )}
                            {settlement.notes && (
                              <p className="text-xs text-gray-600 mt-1">
                                {settlement.notes}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-left">
                          <p className={`text-lg font-bold ${
                            settlement.type === 'payout' ? 'text-red-600' : 'text-green-600'
                          }`}>
                            {settlement.type === 'payout' ? '-' : '+'}{formatCurrency(settlement.amount)}
                          </p>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            settlement.status === 'completed' ? 'bg-green-100 text-green-800' :
                            settlement.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {settlement.status === 'completed' ? 'مكتمل' :
                             settlement.status === 'pending' ? 'معلق' : 'فشل'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {settlements.length === 0 && (
                    <div className="p-8 text-center text-gray-500">
                      لا يوجد تسويات لهذا البائع
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="p-8 text-center text-gray-500">
                اختر بائعاً لعرض سجل التسويات
              </div>
            )}
          </div>
        </div>

        {/* Create Settlement Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h3 className="text-xl font-bold mb-4">إنشاء تسوية جديدة</h3>
              
              <form onSubmit={handleCreateSettlement} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    المبلغ
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                  />
                  {balance && (
                    <p className="text-xs text-gray-500 mt-1">
                      الرصيد المتاح: {formatCurrency(balance.balance)}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    النوع
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="payout">تحويل</option>
                    <option value="adjustment">تعديل</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    طريقة الدفع
                  </label>
                  <select
                    value={formData.payment_method}
                    onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="bank_transfer">تحويل بنكي</option>
                    <option value="cash">نقدي</option>
                    <option value="other">أخرى</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    رقم المرجع
                  </label>
                  <input
                    type="text"
                    value={formData.reference_number}
                    onChange={(e) => setFormData({...formData, reference_number: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="رقم المرجع (اختياري)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ملاحظات
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="ملاحظات إضافية (اختياري)"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition"
                  >
                    إنشاء التسوية
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition"
                  >
                    إلغاء
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
    </AdminLayout>
  );
};

export default Settlements;
